import * as yup from "yup";

/**
 * Length limits from RFC 5321 §4.5.3.1. A regex cannot count, so these are
 * enforced separately in `isValidEmail`.
 */
export const MAX_EMAIL_LENGTH = 254;
export const MAX_EMAIL_LOCAL_LENGTH = 64;

/**
 * EMAIL_REGEX — the pragmatic industry standard.
 *
 * CHANGE: replaces `/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.(com|org|net|in|co\.in)$/i`,
 * which hardcoded a five-entry TLD whitelist and so rejected every real address
 * on `.io`, `.dev`, `.app`, `.ai`, `.co.uk` and the ~1500 other live TLDs.
 *
 * This is the WHATWG HTML living-standard email pattern — the same shape
 * browsers apply to `<input type="email">` — tightened with the one rule a
 * business sign-up needs: the domain must be dotted and end in an alphabetic
 * TLD, so `user@localhost` and `user@intranet` are refused.
 *
 * WHAT IT ALLOWS, and why:
 *
 *   Local part (before the @)
 *     A-Z a-z 0-9 and  ! # $ % & ' * + / = ? ^ _ ` { | } ~ -
 *     Dots may SEPARATE those runs but may not lead, trail, or repeat, so
 *     `first.last` passes while `.a`, `a.`, and `a..b` do not.
 *     `+` is kept deliberately: plus-addressing (`kiran+hr@example.com`) is in
 *     everyday use and silently rejecting it looks like a broken form.
 *
 *   Domain (after the @)
 *     One or more dot-separated labels. Each label is 1-63 characters of
 *     A-Z a-z 0-9 and may contain hyphens INSIDE it but never at either end —
 *     which is what makes `procure-wiz.com` valid and `-wiz.com` / `wiz-.com`
 *     invalid. Underscores are not legal in a hostname and are rejected.
 *
 *   TLD
 *     2-63 letters. Digits are excluded because no TLD is numeric, and the
 *     2-character floor rejects typos like `user@example.c`.
 *
 * WHAT IT DELIBERATELY REFUSES, though RFC 5322 permits it:
 *   - quoted local parts, e.g. `"john doe"@example.com`
 *   - IP-literal domains, e.g. `user@[192.168.0.1]`
 *   - comments, e.g. `user(note)@example.com`
 *   No mainstream provider issues such addresses, and accepting them widens
 *   the input surface for no practical gain.
 *
 * Non-ASCII (IDN) domains such as `user@münchen.de` must be supplied in their
 * punycode form (`user@xn--mnchen-3ya.de`), which this accepts.
 *
 * NOTE: no `i` flag — the character classes already list both cases, and an
 * `i` flag on a class that spells out ranges only invites the belief that some
 * other part is case-sensitive.
 */
export const EMAIL_REGEX =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;

/**
 * True when `email` is an address this application will accept.
 *
 * Trims first, because a trailing space from a paste is a formatting slip
 * rather than an invalid address.
 */
export const isValidEmail = (email: string): boolean => {
  const value = email.trim();

  if (value.length === 0 || value.length > MAX_EMAIL_LENGTH) return false;

  // The local part is everything before the LAST @, matching how the regex and
  // every mail server read it.
  const at = value.lastIndexOf("@");
  if (at === -1 || at > MAX_EMAIL_LOCAL_LENGTH) return false;

  return EMAIL_REGEX.test(value);
};

export const validateNoTrailingOrLeadingWhitespace = (fieldLabel: string) =>
  yup
    .string()
    .trim()
    .required(`${fieldLabel} is Required`)
    // .test(
    //   `not-only-spaces-${fieldLabel}`,
    //   "Empty whitespace not allowed in start and end",
    //   (value) => !!value && value.trim().length > 0
    // )
    // .test(
    //   `no-leading-trailing-space-${fieldLabel}`,
    //   "Empty whitespace not allowed in start and end",
    //   (value) => !!value && value === value.trim()
    // );


export const TrailingOrLeadingWhitespaceValidation = (fieldLabel: string) =>
  yup
    .string()
    // Do not use .trim() here, otherwise Yup mutates the string before the tests run
    .required(`${fieldLabel} is Required`)
    .test(
      `not-only-spaces-${fieldLabel}`,
      `${fieldLabel} cannot be only blank spaces`,
      (value) => !!value && value.trim().length > 0
    )
    .test(
      `no-leading-trailing-space-${fieldLabel}`,
      "Empty whitespace not allowed in start and end",
      (value) => !!value && value === value.trim()
    );

export const validateNoLeadingTrailingWhitespace = (value?: string) => {
  if (!value) return true; // Let .required() handle empty states
  return value.trim().length > 0; // Fails if the user enters ONLY spaces
};

/**
 * Formats IFSC input by removing non-alphanumeric characters 
 * and forcing the string to uppercase.
 */
export const handleIFSCInput = (e: React.FormEvent<HTMLInputElement>) => {
  const target = e.target as HTMLInputElement;

  // Replace anything that is NOT a letter or number, then uppercase it
  const formattedValue = target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

  // Enforce max length of 11 at the DOM level (optional but recommended)
  target.value = formattedValue.slice(0, 11);
};

/**
 * Validates an Indian Financial System Code (IFSC)
 * Standard: 4 Alphabets + 1 Zero + 6 Alphanumeric characters (Total exactly 11)
 */
export const validateIFSCCode = (ifsc: string | undefined | null): boolean => {
  if (!ifsc) return false;

  // Trim to ignore accidental leading/trailing spaces
  const cleanIFSC = ifsc.trim();

  // Regex pattern for IFSC
  const ifscRegex = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;

  return ifscRegex.test(cleanIFSC);
};